/**
 * Pricing Configuration Loader
 *
 * Loads pricing configuration from Supabase with caching.
 * Supports per-product configs (hotel vs flight).
 * Falls back to defaults on error.
 *
 * @module pricing-config
 */

import { supabase } from '@/lib/supabase';
import {
  DEFAULT_CONFIG,
  derivePricingRules,
  getTierFromSubscription,
  type PricingConfig,
  type TierId,
  type PricingRules,
} from './subscription-rules';
import type { UserSubscription } from '@/types/subscription';

// ============================================
// Types
// ============================================

export type ProductType = 'all' | 'hotel' | 'flight';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// ============================================
// Cache Configuration
// ============================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Per-product config cache (keyed by product type)
const configCacheMap: Map<ProductType, CacheEntry<PricingConfig>> = new Map();
const tierRulesCache: Map<string, CacheEntry<Partial<PricingRules>>> = new Map();
// In-flight tier override lookups, so concurrent cold-cache callers share one request
const tierRulesInFlight: Map<string, Promise<Partial<PricingRules> | null>> = new Map();
// Bumped by clearConfigCache so a request that started before the clear cannot refill the cache
let tierRulesGeneration = 0;

// ============================================
// Cache Management
// ============================================

/**
 * Clear all pricing config caches
 */
export function clearConfigCache(): void {
  configCacheMap.clear();
  tierRulesCache.clear();
  tierRulesInFlight.clear();
  tierRulesGeneration += 1;
}

/**
 * Check if a cache entry is still valid
 */
function isCacheValid<T>(cache: CacheEntry<T> | null | undefined): cache is CacheEntry<T> {
  if (!cache) return false;
  return Date.now() - cache.timestamp < CACHE_TTL_MS;
}

// ============================================
// Config Loading Functions
// ============================================

/**
 * Fetch pricing configuration from Supabase for a specific product.
 * Tries product-specific row first, falls back to 'all' default.
 * Falls back to DEFAULT_CONFIG on error.
 */
export async function fetchPricingConfig(product: ProductType = 'all'): Promise<PricingConfig> {
  // Return cached value if valid
  const cached = configCacheMap.get(product);
  if (isCacheValid(cached)) {
    return cached.data;
  }

  try {
    // Try product-specific config first (e.g., product='flight')
    let data = null;

    if (product !== 'all') {
      const result = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('tier', 'default')
        .eq('product', product)
        .is('hotel_id', null)
        .is('category', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!result.error) {
        data = result.data;
      }
    }

    // Fall back to default/all config
    if (!data) {
      const result = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('tier', 'default')
        .eq('product', 'all')
        .is('hotel_id', null)
        .is('category', null)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (result.error) {
        console.error('[pricing-config] Error fetching config:', result.error);
        return DEFAULT_CONFIG;
      }
      data = result.data;
    }

    if (!data) {
      console.warn('[pricing-config] No config found for product:', product);
      return DEFAULT_CONFIG;
    }

    const rawMarkup = Number(data.markup_percentage);
    const rawFee = Number(data.transaction_fee_percentage);
    const config: PricingConfig = {
      markupPercentage: Number.isFinite(rawMarkup) ? rawMarkup : DEFAULT_CONFIG.markupPercentage,
      transactionFeePercentage: Number.isFinite(rawFee) ? rawFee : DEFAULT_CONFIG.transactionFeePercentage,
      paywallEnabled: data.paywall_enabled ?? DEFAULT_CONFIG.paywallEnabled,
    };

    // Update cache
    configCacheMap.set(product, {
      data: config,
      timestamp: Date.now(),
    });

    return config;
  } catch (error) {
    console.error('[pricing-config] Failed to fetch pricing config:', error);
    return DEFAULT_CONFIG;
  }
}

/**
 * Generate cache key for tier/product/hotel/category combination
 */
function getTierCacheKey(tier: string, product: ProductType, hotelId?: string, category?: string): string {
  return `${tier}:${product}:${hotelId || ''}:${category || ''}`;
}

/**
 * Fetch the newest pricing_rules row for one (tier, product) at the given scope.
 * Uses order + limit(1) so duplicated rows never make maybeSingle() error out.
 */
async function fetchTierRow(
  tier: TierId,
  product: ProductType,
  hotelId?: string,
  category?: string
) {
  let query = supabase
    .from('pricing_rules')
    .select('*')
    .eq('tier', tier)
    .eq('product', product);

  if (hotelId) {
    // Hotel-specific override
    query = query.eq('hotel_id', hotelId);
  } else if (category) {
    // Category-specific override
    query = query.eq('category', category).is('hotel_id', null);
  } else {
    // Tier-level override
    query = query.is('hotel_id', null).is('category', null);
  }

  return query.order('updated_at', { ascending: false }).limit(1).maybeSingle();
}

/**
 * Fetch tier-specific overrides from Supabase.
 *
 * Resolution matches the backend `getPricingRule` (pricingService.js):
 * `(tier, product)` first, then `(tier, 'all')`. Tiers like free/pro have several
 * canonical rows (all/hotel/flight), so the lookup must be scoped by product.
 *
 * Omitting `product` (default 'all') reads the tier-wide `(tier, 'all')` row, which is
 * the right source for tier-wide policy fields (ticket limit, beneficiary policy).
 *
 * Returns the raw row mapping; `derivePricingRules` decides which fields may apply
 * (see TIER_OVERRIDE_FIELDS). Returns null if no override found.
 */
export interface TierOverrideOptions {
  product?: ProductType;
  hotelId?: string;
  category?: string;
}

export async function fetchTierOverrides(
  tier: TierId,
  { product = 'all', hotelId, category }: TierOverrideOptions = {}
): Promise<Partial<PricingRules> | null> {
  const cacheKey = getTierCacheKey(tier, product, hotelId, category);
  const cached = tierRulesCache.get(cacheKey);

  // Return cached value if valid (an empty object means "no override")
  if (isCacheValid(cached)) {
    return Object.keys(cached.data).length > 0 ? cached.data : null;
  }

  const inFlight = tierRulesInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const generation = tierRulesGeneration;
  const request = loadTierOverride(tier, product, cacheKey, generation, hotelId, category).finally(() => {
    if (tierRulesInFlight.get(cacheKey) === request) tierRulesInFlight.delete(cacheKey);
  });
  tierRulesInFlight.set(cacheKey, request);
  return request;
}

async function loadTierOverride(
  tier: TierId,
  product: ProductType,
  cacheKey: string,
  generation: number,
  hotelId?: string,
  category?: string
): Promise<Partial<PricingRules> | null> {
  try {
    const primary = await fetchTierRow(tier, product, hotelId, category);
    let data = primary.data;

    if (primary.error) {
      console.error('[pricing-config] Error fetching tier overrides:', primary.error);
      return null;
    }

    // No product-specific row: fall back to the tier-wide 'all' row
    let resolvedFromAll = false;
    if (!data && product !== 'all') {
      const fallback = await fetchTierRow(tier, 'all', hotelId, category);
      if (fallback.error) {
        console.error('[pricing-config] Error fetching tier overrides:', fallback.error);
        return null;
      }
      data = fallback.data;
      resolvedFromAll = true;
    }

    if (!data) {
      // No override found, cache null result
      if (generation === tierRulesGeneration) {
        tierRulesCache.set(cacheKey, { data: {}, timestamp: Date.now() });
      }
      return null;
    }

    const override: Partial<PricingRules> = {
      canHaveSpecialPrice: data.special_price_enabled,
      ticketLimit: data.ticket_limit,
      beneficiaryPolicy: data.beneficiary_policy as 'self-only' | 'any',
      paywallEnabled: data.paywall_enabled,
    };
    // A null/invalid markup must not become a 0% override (Number(null) === 0)
    if (data.markup_percentage != null) {
      const rawMarkup = Number(data.markup_percentage);
      if (Number.isFinite(rawMarkup)) override.markupPercentage = rawMarkup;
    }

    // Update cache (a fallback row also warms the tier-wide 'all' slot)
    const entry = { data: override, timestamp: Date.now() };
    if (generation !== tierRulesGeneration) return override;
    tierRulesCache.set(cacheKey, entry);
    if (resolvedFromAll) {
      tierRulesCache.set(getTierCacheKey(tier, 'all', hotelId, category), entry);
    }

    return override;
  } catch (error) {
    console.error('[pricing-config] Failed to fetch tier overrides:', error);
    return null;
  }
}

/**
 * Synchronously read a cached tier override (populated by fetchTierOverrides).
 * Returns undefined when nothing is cached yet, null when the tier has no override row.
 */
export function getCachedTierOverrideSync(
  tier: TierId,
  { product = 'all', hotelId, category }: TierOverrideOptions = {}
): Partial<PricingRules> | null | undefined {
  const cached = tierRulesCache.get(getTierCacheKey(tier, product, hotelId, category));
  if (!isCacheValid(cached)) return undefined;
  return Object.keys(cached.data).length > 0 ? cached.data : null;
}

/**
 * Resolve the pricing rules for a subscription: product config + the tier's DB override.
 * Use this instead of calling fetchPricingConfig + derivePricingRules by hand, so every
 * surface (cards, cart, checkout) applies the same tier override.
 *
 * The override is scoped by product only, like the backend getPricingRule (which never
 * reads hotel/category rows). Without a product it reads the tier-wide `(tier, 'all')`
 * row — the right source for cart/checkout validation (ticket limit, beneficiary policy).
 */
export async function resolvePricingRules(
  subscription: UserSubscription | null | undefined,
  { product }: { product?: ProductType } = {}
): Promise<PricingRules> {
  const tier = getTierFromSubscription(subscription).id;
  const [config, tierOverride] = await Promise.all([
    fetchPricingConfig(product),
    fetchTierOverrides(tier, { product }),
  ]);
  return derivePricingRules(subscription, config, {
    tierOverride: tierOverride ?? undefined,
  });
}

/**
 * Synchronously get cached config or return defaults.
 * Optionally specify product type to get product-specific cached config.
 */
export function getCachedConfigSync(product: ProductType = 'all'): PricingConfig {
  const cached = configCacheMap.get(product);
  if (isCacheValid(cached)) {
    return cached.data;
  }
  // Fall back to 'all' cache if product-specific not available
  if (product !== 'all') {
    const allCached = configCacheMap.get('all');
    if (isCacheValid(allCached)) {
      return allCached.data;
    }
  }
  return DEFAULT_CONFIG;
}

/**
 * Preload the pricing config into cache
 * Call this early in app lifecycle
 */
export async function preloadPricingConfig(): Promise<void> {
  try {
    await Promise.all([
      fetchPricingConfig('all'),
      fetchPricingConfig('flight'),
    ]);
  } catch (error) {
    console.error('[pricing-config] Failed to preload config:', error);
  }
}
